import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useParams } from 'react-router-dom'
import {
  confirmLearnerSummary,
  fetchIperiaStatus,
  submitDocuments,
  submitNir,
  updateLearnerSummary,
  updateParentEmployeur,
  uploadDocument,
} from '../services/iperiaStatus'
import type { IperiaStatusPayload } from '../types/iperiaStatus'
import Bugsnag from '@bugsnag/js'

type IperiaStatusContextValue = {
  status: IperiaStatusPayload | null
  loading: boolean
  error: Error | null
  refetch: () => Promise<void>
  updateSummary: (
    learnerSummary: Partial<IperiaStatusPayload['learner_summary']>,
  ) => Promise<IperiaStatusPayload>
  confirmSummary: (
    learnerSummary?: IperiaStatusPayload['learner_summary'],
  ) => Promise<IperiaStatusPayload>
  uploadDocument: (file: File, documentType: string) => Promise<IperiaStatusPayload>
  submitDocuments: () => Promise<IperiaStatusPayload>
  updateParentEmployeur: (data: Record<string, any>) => Promise<IperiaStatusPayload>
  submitNir: (nir: string, authorizationDataUse: boolean) => Promise<IperiaStatusPayload>
}

const IperiaStatusContext = createContext<IperiaStatusContextValue | null>(null)

export function IperiaStatusProvider({ children }: { children: ReactNode }) {
  const hasLoadedOnceRef = useRef<boolean>(false)
  const loadedForGlobalIdRef = useRef<string | undefined>(undefined)
  
  const { global_id: globalId } = useParams<{ global_id: string }>()
  const [status, setStatus] = useState<IperiaStatusPayload | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const refetch = useCallback(async () => {
    if (!globalId) {
      setLoading(false)
      setError(new Error('Missing global_id'))
      return
    }

    const isSameDossier = loadedForGlobalIdRef.current === globalId

    // Do not flip loading back to true on background refreshes: Signature embeds an
    // Edusign iframe that remounts (and looks like it refreshes) whenever loading gates the UI.
    // A new `global_id` is a different dossier (CTA « Formation suivante ») : là on reset.
    try {
      const data = await fetchIperiaStatus(globalId)
      loadedForGlobalIdRef.current = globalId
      hasLoadedOnceRef.current = true
      setStatus(data)
      setError(null)
    } catch (err) {
      const failure = err instanceof Error ? err : new Error('Failed to fetch IPERIA status')
      if (hasLoadedOnceRef.current && isSameDossier) {
        // Signature and Documents poll through this path: a transient failure must keep
        // the last payload, otherwise an in-progress step is replaced by an error screen.
        Bugsnag.notify(failure)
      } else {
        setStatus(null)
        setError(failure)
      }
    } finally {
      setLoading(false)
    }
  }, [globalId])

  useEffect(() => {
    if (loadedForGlobalIdRef.current !== globalId) {
      hasLoadedOnceRef.current = false
      setStatus(null)
      setLoading(true)
      setError(null)
    }
    void refetch()
  }, [globalId, refetch])

  const updateSummary = useCallback(
    async (learnerSummary: Partial<IperiaStatusPayload['learner_summary']>) => {
      if (!globalId) {
        throw new Error('Missing global_id')
      }
      const updated = await updateLearnerSummary(globalId, learnerSummary)
      setStatus(updated)
      return updated
    },
    [globalId],
  )

  const confirmSummary = useCallback(
    async (learnerSummary?: IperiaStatusPayload['learner_summary']) => {
      if (!globalId) {
        throw new Error('Missing global_id')
      }
      const updated = await confirmLearnerSummary(globalId, learnerSummary)
      setStatus(updated)
      return updated
    },
    [globalId],
  )

  const uploadDocumentStep = useCallback(async (file: File, documentType: string) => {
    if (!globalId) {
      throw new Error('Missing global_id')
    }
    const updated = await uploadDocument(globalId, file, documentType)
    setStatus(updated)
    return updated
  }, [globalId])

  const submitDocumentsStep = useCallback(async () => {
    if (!globalId) {
      throw new Error('Missing global_id')
    }
    const updated = await submitDocuments(globalId)
    setStatus(updated)
    return updated
  }, [globalId])

  const updateParentEmployeurStep = useCallback(
    async (data: Record<string, any>) => {
      if (!globalId) {
        throw new Error('Missing global_id')
      }
      const updated = await updateParentEmployeur(globalId, data)
      setStatus(updated)
      return updated
    },
    [globalId],
  )

  const submitNirStep = useCallback(async (nir: string, authorizationDataUse: boolean) => {
    if (!globalId) {
      throw new Error('Missing global_id')
    }
    const updated = await submitNir(globalId, nir, authorizationDataUse)
    setStatus(updated)
    return updated
  }, [globalId])

  const value = useMemo(
    () => ({
      status,
      loading,
      error,
      refetch,
      updateSummary,
      confirmSummary,
      uploadDocument: uploadDocumentStep,
      submitDocuments: submitDocumentsStep,
      updateParentEmployeur: updateParentEmployeurStep,
      submitNir: submitNirStep,
    }),
    [status, loading, error, refetch, updateSummary, confirmSummary, uploadDocumentStep, submitDocumentsStep, updateParentEmployeurStep, submitNirStep],
  )

  return <IperiaStatusContext.Provider value={value}>{children}</IperiaStatusContext.Provider>
}

export function useIperiaStatus(): IperiaStatusContextValue {
  const context = useContext(IperiaStatusContext)
  if (!context) {
    throw new Error('useIperiaStatus must be used within IperiaStatusProvider')
  }
  return context
}

/**
 * Variante tolérante, pour ce que la mise en page rend en dehors du parcours de l'apprenante :
 * `EdumiamLayout` habille aussi le portail du parent employeur, qui n'a pas de dossier chargé.
 */
export function useOptionalIperiaStatus(): IperiaStatusContextValue | null {
  return useContext(IperiaStatusContext)
}
